import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, Trash2, LogOut, ShieldCheck, Volume2, Play, Stethoscope, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import LanguageProfiles from "@/components/LanguageProfiles";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import SerbianScriptSelect from "@/components/SerbianScriptSelect";
import InterfaceLanguageSelect from "@/components/InterfaceLanguageSelect";
import { useI18n } from "@/context/I18nContext";
import { langName } from "@/lib/constants";

const VOICES = [
  { id: "nova", label: "Nova (warm)" },
  { id: "shimmer", label: "Shimmer (bright)" },
  { id: "alloy", label: "Alloy (neutral)" },
  { id: "echo", label: "Echo (calm)" },
  { id: "fable", label: "Fable (expressive)" },
  { id: "onyx", label: "Onyx (deep)" },
];

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [voice, setVoice] = useState(user?.tts_voice || "nova");
  const [speed, setSpeed] = useState(user?.tts_speed || 1.0);
  const [savingVoice, setSavingVoice] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const isPro = user?.has_pro_access;

  // --- Temporary Owner diagnostic (safe: no secret values) ---
  const [diag, setDiag] = useState(null);
  const [meResp, setMeResp] = useState(null);
  const [diagErr, setDiagErr] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);

  const runDiag = async () => {
    setLoadingDiag(true);
    setDiagErr(null);
    try {
      const [oc, me] = await Promise.all([
        api.get("/auth/owner-check"),
        api.get("/auth/me"),
      ]);
      setDiag(oc.data);
      setMeResp(me.data);
    } catch (e) {
      setDiagErr(e?.response?.data?.detail || e.message || "Request failed");
    } finally {
      setLoadingDiag(false);
    }
  };

  useEffect(() => {
    runDiag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveVoice = async () => {
    setSavingVoice(true);
    try {
      const res = await api.patch("/preferences", { tts_voice: voice, tts_speed: speed });
      setUser(res.data);
      toast.success("Voice saved!");
    } catch { toast.error("Could not save."); }
    finally { setSavingVoice(false); }
  };

  const preview = async () => {
    if (!isPro) { toast.error("Voice preview is a Pro feature."); return; }
    setPreviewing(true);
    try {
      const res = await api.post("/tts", { text: "Hello! This is your FluentLoop narrator.", voice, speed });
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      audio.onended = () => setPreviewing(false);
      audio.onerror = () => setPreviewing(false);
      await audio.play();
    } catch { setPreviewing(false); toast.error("Preview failed."); }
  };

  const exportData = async () => {
    try {
      const res = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "fluent-data-export.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported.");
    } catch { toast.error("Export failed."); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/account");
      toast.success("Account deleted.");
      logout();
      navigate("/");
    } catch { toast.error("Deletion failed."); }
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl">{t("settings.title")}</h1>

        <div className="nb-card p-6 mt-6" data-testid="interface-language-card">
          <h3 className="font-cabinet font-bold text-xl flex items-center gap-2">{t("settings.interfaceLanguage")}</h3>
          <p className="text-sm text-gray-600 mt-1">{t("settings.interfaceLanguageHelp")}</p>
          <div className="mt-4 max-w-xs">
            <InterfaceLanguageSelect />
          </div>
        </div>

        <div className="nb-card p-6 mt-6">
          <h3 className="font-cabinet font-bold text-xl">Profile</h3>
          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
            <Info label="Name" value={user?.name} />
            <Info label="Email" value={user?.email} />
            <Info label="Learning" value={langName(user?.target_lang)} />
            <Info label="Level" value={user?.level} />
            <Info label="Native" value={langName(user?.native_lang)} />
            <Info label="Plan" value={user?.is_owner ? "Owner · Lifetime" : user?.is_lifetime ? "Lifetime" : user?.access_source === "education" ? "Education · School Access" : user?.has_pro_access ? "Pro" : "Free"} />
            <Info label="Role" value={user?.platform_role || "individual"} />
            <Info label="Access source" value={user?.access_source || "free"} />
          </div>
          {user?.access_source === "education" && user?.education_expires_at && (
            <div className="mt-4 pt-4 border-t-2 border-dashed border-ink/20 text-sm" data-testid="settings-education-until">
              <span className="font-bold">Education access until</span> {new Date(user.education_expires_at).toLocaleDateString()}
            </div>
          )}
          {!user?.has_pro_access && (
            <a href="/education" className="nb-btn-outline mt-4 py-2 px-4 text-sm inline-flex" data-testid="settings-education-link">🎓 FluentLoop for Education — 90-day school pilot, free for verified schools</a>
          )}
          <div className="mt-4 pt-4 border-t-2 border-dashed border-ink/20">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-400">Account ID (immutable)</div>
            <div className="font-mono text-sm break-all mt-1" data-testid="settings-account-id">{user?.user_id || "—"}</div>
          </div>
        </div>

        {user?.target_lang === "sr" && (
          <div className="nb-card p-6 mt-6" id="serbian-script" data-testid="settings-serbian-script-card">
            <SerbianScriptSelect value={user.target_script || "cyrillic"} testid="settings-serbian-script"
              onChange={async (v) => {
                try { const r = await api.patch("/auth/target-script", { target_script: v }); setUser(r.data); toast.success(t("wordExplorer.scriptSaved")); }
                catch { toast.error(t("wordExplorer.failed")); }
              }} />
          </div>
        )}

        <SubscriptionCard />

        <LanguageProfiles />

        {user?.is_owner && (
          <Link to="/admin/usage" className="nb-card p-5 mt-6 flex items-center justify-between hover:-translate-y-0.5 transition-transform" data-testid="ai-usage-link">
            <div><div className="font-cabinet font-bold text-lg">AI Usage</div><div className="text-sm text-gray-500">Owner-only AI generation &amp; conversation observability</div></div>
            <span className="nb-tag bg-lavender">Open →</span>
          </Link>
        )}

        <div className="nb-card border-ink p-6 mt-6" data-testid="owner-diagnostic-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-cabinet font-bold text-xl flex items-center gap-2"><Stethoscope className="w-5 h-5" /> Owner diagnostic <span className="nb-tag bg-gray-100 text-gray-500 text-[10px]">temporary</span></h3>
            <button onClick={runDiag} disabled={loadingDiag} className="nb-btn-outline py-2 px-3 text-sm" data-testid="owner-diag-refresh">
              <RefreshCw className={`w-4 h-4 ${loadingDiag ? "animate-spin" : ""}`} /> {loadingDiag ? "Checking…" : "Re-run"}
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Authenticated check via the same API client as the app. No secret values are shown.</p>

          {diagErr && (
            <div className="nb-card bg-peach p-3 mt-4 text-sm" data-testid="owner-diag-error">Error: {String(diagErr)}</div>
          )}

          {diag && (
            <div className="grid sm:grid-cols-2 gap-6 mt-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">/api/auth/owner-check</div>
                <div className="space-y-1.5">
                  <DiagRow k="owner_var_present" v={diag.owner_var_present} tid="dv-owner_var_present" />
                  <DiagRow k="owner_configured" v={diag.owner_configured} tid="dv-owner_configured" />
                  <DiagRow k="owner_is_placeholder" v={diag.owner_is_placeholder} tid="dv-owner_is_placeholder" />
                  <DiagRow k="matches_your_id" v={diag.matches_your_id} tid="dv-matches_your_id" highlight />
                  <DiagRow k="platform_role" v={diag.resolved_platform_role} tid="dv-oc-platform_role" />
                  <DiagRow k="access_source" v={diag.resolved_access_source} tid="dv-oc-access_source" />
                  <DiagRow k="has_pro_access" v={diag.resolved_has_pro_access} tid="dv-oc-has_pro_access" />
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">/api/auth/me</div>
                <div className="space-y-1.5">
                  <DiagRow k="user_id" v={meResp?.user_id} tid="dv-me-user_id" mono />
                  <DiagRow k="platform_role" v={meResp?.platform_role} tid="dv-me-platform_role" />
                  <DiagRow k="access_source" v={meResp?.access_source} tid="dv-me-access_source" />
                  <DiagRow k="has_pro_access" v={meResp?.has_pro_access} tid="dv-me-has_pro_access" />
                  <DiagRow k="is_owner" v={meResp?.is_owner} tid="dv-me-is_owner" highlight />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="nb-card p-6 mt-6">
          <h3 className="font-cabinet font-bold text-xl flex items-center gap-2"><Volume2 className="w-5 h-5" /> Narrator voice</h3>
          <p className="text-sm text-gray-600 mt-1">Choose the voice used for Story narration and Roommate replies. {!isPro && <span className="text-coral font-bold">Pro feature.</span>}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Voice</label>
              <select className="nb-input mt-1" value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="voice-select">
                {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Speed · {Number(speed).toFixed(2)}x</label>
              <input type="range" min="0.7" max="1.3" step="0.05" value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))} className="w-full mt-3 accent-coral" data-testid="voice-speed" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={preview} disabled={previewing} className="nb-btn-outline flex-1 py-3" data-testid="voice-preview-btn"><Play className="w-4 h-4" /> {previewing ? "Playing…" : "Preview"}</button>
            <button onClick={saveVoice} disabled={savingVoice} className="nb-btn-primary flex-1 py-3" data-testid="voice-save-btn">{savingVoice ? "Saving…" : "Save voice"}</button>
          </div>
        </div>

        <div className="nb-card p-6 mt-6">
          <h3 className="font-cabinet font-bold text-xl flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Your data (GDPR)</h3>
          <p className="text-sm text-gray-600 mt-1">We never store your photos or voice recordings — only the words and scores derived from them.</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button onClick={exportData} className="nb-btn-outline flex-1 py-3" data-testid="export-data-btn"><Download className="w-5 h-5" /> Export my data</button>
            <button onClick={() => logout().then(() => navigate("/"))} className="nb-btn-secondary flex-1 py-3" data-testid="logout-btn"><LogOut className="w-5 h-5" /> Log out</button>
          </div>
        </div>

        <div className="nb-card border-coral p-6 mt-6">
          <h3 className="font-cabinet font-bold text-xl text-coral">Danger zone</h3>
          <p className="text-sm text-gray-600 mt-1">Permanently delete your account and all associated data. This cannot be undone.</p>
          {!confirming ? (
            <button onClick={() => setConfirming(true)} className="nb-btn-outline mt-4 py-3 px-5 text-coral border-coral" data-testid="delete-account-btn"><Trash2 className="w-5 h-5" /> Delete account</button>
          ) : (
            <div className="flex gap-3 mt-4">
              <button onClick={deleteAccount} className="nb-btn-primary py-3 px-5" data-testid="confirm-delete-btn">Yes, delete everything</button>
              <button onClick={() => setConfirming(false)} className="nb-btn-outline py-3 px-5">Cancel</button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</div><div className="font-cabinet font-bold">{value || "—"}</div></div>
);

const DiagRow = ({ k, v, tid, mono, highlight }) => {
  const isBool = typeof v === "boolean";
  const display = v === undefined || v === null ? "—" : String(v);
  const color = isBool
    ? (v ? "bg-mint text-ink" : "bg-peach text-ink")
    : "bg-gray-100 text-ink";
  return (
    <div className={`flex items-center justify-between gap-3 ${highlight ? "font-bold" : ""}`}>
      <span className="text-xs font-mono text-gray-500">{k}</span>
      <span className={`nb-tag ${color} ${mono ? "font-mono text-[10px] break-all" : ""}`} data-testid={tid}>{display}</span>
    </div>
  );
};
